// src/pages/activity/ActivityPage.tsx
// Activity hub — inbox (home), feed (browse by scope), decisions (journal).
// Detail is a right rail shared across all three tabs.
// See docs/ux/activity-ia.md.

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { activityInboxStore, activityStore } from '@core/stores';
import { Badge } from '@react/components';
import type { Activity, InboxCounts } from '@core/types/activity';
import { InboxTab } from '@/features/activity/InboxTab';
import { FeedTab } from '@/features/activity/FeedTab';
import { DecisionsTab } from '@/features/activity/DecisionsTab';
import { ActivityDetailRail } from '@/features/activity/ActivityDetailRail';

type Tab = 'inbox' | 'feed' | 'decisions';

export const ActivityPage = observer(() => {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>('inbox');
  const [counts, setCounts] = useState<InboxCounts | null>(null);
  const [selected, setSelected] = useState<Activity | null>(null);

  const loadCounts = () => {
    activityInboxStore.GetInboxCounts().then((res) => setCounts(res.counts ?? null)).catch(() => setCounts(null));
  };

  useEffect(() => { loadCounts(); }, []);

  // Deep link support: ?open=<id> opens the detail rail for an activity not
  // necessarily present in the loaded list/page.
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId && (!selected || selected.entity?.id !== openId)) {
      activityStore.GetActivity(openId).then(setSelected).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openActivity = (activity: Activity) => {
    setSelected(activity);
    if (activity.entity?.id) setSearchParams({ open: activity.entity.id }, { replace: true });
  };

  const closeDetail = () => {
    setSelected(null);
    searchParams.delete('open');
    setSearchParams(searchParams, { replace: true });
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'inbox', label: t('activity.tabs.inbox') },
    { id: 'feed', label: t('activity.tabs.feed') },
    { id: 'decisions', label: t('activity.tabs.decisions') },
  ];

  return (
    <div className="flex h-full">
      <div className="min-w-0 flex-1 space-y-4 overflow-y-auto p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-xl font-semibold text-foreground">{t('activity.page.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('activity.page.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            {counts && counts.total > 0 && (
              <Badge variant="primary">{t('activity.inbox.counts.total', { count: counts.total })}</Badge>
            )}
            <Link to="/activity/settings" className="text-sm text-primary hover:underline">{t('activity.settings.title')}</Link>
          </div>
        </div>

        <div className="flex items-center gap-1 border-b border-border" role="tablist">
          {tabs.map((tb) => (
            <button
              key={tb.id}
              type="button"
              role="tab"
              aria-selected={tab === tb.id}
              onClick={() => setTab(tb.id)}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                tab === tb.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {tab === 'inbox' && (
          <InboxTab
            onOpenActivity={openActivity}
            selectedId={selected?.entity?.id}
            onAcknowledged={loadCounts}
          />
        )}
        {tab === 'feed' && <FeedTab onOpenActivity={openActivity} selectedId={selected?.entity?.id} />}
        {tab === 'decisions' && <DecisionsTab onOpenActivity={openActivity} selectedId={selected?.entity?.id} />}
      </div>

      {selected && <ActivityDetailRail activity={selected} onClose={closeDetail} />}
    </div>
  );
});

export default ActivityPage;
